/**
 * Small HUD building blocks: buttons, segmented switches, inputs, meters, chips.
 * Everything is styled as war-room hardware (bakelite, brass) — see controls.module.css.
 */
import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { bus } from '../../game/bus';
import s from './controls.module.css';
import { Icon, type IconName } from './Icon';

const cx = (...c: (string | false | null | undefined)[]): string => c.filter(Boolean).join(' ');

export const click = (): void => bus.emit('sound', { name: 'click' });

// ---- Button -------------------------------------------------------------------------------------

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** `ink`: a quiet text button for paper documents (dark ink on paper). */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'ink';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  hotkey?: string;
  active?: boolean;
  block?: boolean;
  /** Suppress the click sound (e.g. for buttons that play their own). */
  silent?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  hotkey,
  active,
  block,
  silent,
  className,
  children,
  onClick,
  ...rest
}: ButtonProps) {
  const iconOnly = !children && !!icon;
  return (
    <button
      type="button"
      className={cx(
        s.btn,
        s[variant],
        size !== 'md' && s[size],
        block && s.block,
        iconOnly && s.iconOnly,
        active && s.active,
        className,
      )}
      onClick={(e) => {
        if (!silent) click();
        onClick?.(e);
      }}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === 'lg' ? 18 : size === 'sm' ? 13 : 15} />}
      {children}
      {hotkey && <Keycap>{hotkey}</Keycap>}
    </button>
  );
}

export function Keycap({ children }: { children: ReactNode }) {
  return <kbd className={s.key}>{children}</kbd>;
}

// ---- Segmented ----------------------------------------------------------------------------------

export interface SegOption<T extends string | number> {
  value: T;
  label: ReactNode;
  title?: string;
  disabled?: boolean;
}

export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  block,
  small,
  disabled,
  className,
}: {
  value: T;
  options: SegOption<T>[];
  onChange(v: T): void;
  block?: boolean;
  small?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cx(s.seg, block && s.segBlock, small && s.segSmall, className)}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={o.value === value}
          title={o.title}
          disabled={disabled || o.disabled}
          className={cx(s.segBtn, o.value === value && s.segOn)}
          onClick={() => {
            if (o.value !== value) {
              click();
              onChange(o.value);
            }
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- Field / inputs -----------------------------------------------------------------------------

export function Field({
  label,
  hint,
  row,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  row?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(s.field, row && s.fieldRow, className)}>
      <div>
        <div className={s.label}>{label}</div>
        {hint && row && <div className={s.hint}>{hint}</div>}
      </div>
      {children}
      {hint && !row && <div className={s.hint}>{hint}</div>}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  mono,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
  value: string;
  onChange(v: string): void;
  mono?: boolean;
}) {
  return (
    <input
      className={cx(s.input, mono && s.mono, className)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      {...rest}
    />
  );
}

export function Select<T extends string | number>({
  value,
  options,
  onChange,
  small,
  disabled,
  className,
  title,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange(v: T): void;
  small?: boolean;
  disabled?: boolean;
  className?: string;
  title?: string;
}) {
  return (
    <select
      className={cx(s.select, small && s.selectSm, className)}
      value={String(value)}
      disabled={disabled}
      title={title}
      onChange={(e) => {
        const o = options.find((x) => String(x.value) === e.target.value);
        if (o) {
          click();
          onChange(o.value);
        }
      }}
    >
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange(v: boolean): void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={cx(s.toggle, on && s.toggleOn)}
      onClick={() => {
        click();
        onChange(!on);
      }}
    />
  );
}

export function Slider({
  value,
  min,
  max,
  step,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange(v: number): void;
  label?: string;
}) {
  const fill = `${((value - min) / (max - min)) * 100}%`;
  return (
    <input
      type="range"
      className={s.slider}
      min={min}
      max={max}
      step={step}
      value={value}
      aria-label={label}
      style={{ '--fill': fill } as CSSProperties}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  );
}

// ---- Meters -------------------------------------------------------------------------------------

export type BarTone = 'ink' | 'org' | 'supply' | 'entrench' | 'brass' | 'faction';

/** Green → amber → red ramp for readiness-type values. */
export function rampColor(v: number): string {
  if (v >= 0.66) return '#7fa865';
  if (v >= 0.4) return '#c9a13f';
  if (v >= 0.2) return '#cf7a36';
  return '#c2412f';
}

const TONE_FILL: Record<Exclude<BarTone, 'org' | 'supply' | 'faction'>, string> = {
  ink: 'linear-gradient(180deg, #e3d9c2, #b9ad93)',
  entrench: 'linear-gradient(180deg, #a88d62, #7d6640)',
  brass: 'var(--brass-grad)',
};

export function Bar({
  label,
  value,
  tone = 'ink',
  color,
  display,
  className,
}: {
  label?: ReactNode;
  value: number;
  tone?: BarTone;
  /** Explicit fill for tone 'faction'. */
  color?: string;
  /** Right-hand readout; defaults to a percentage. */
  display?: ReactNode;
  className?: string;
}) {
  const v = Math.max(0, Math.min(1, value));
  const fill =
    tone === 'org' || tone === 'supply'
      ? rampColor(v)
      : tone === 'faction'
        ? (color ?? 'var(--brass)')
        : TONE_FILL[tone];
  return (
    <div
      className={cx(s.bar, className)}
      style={label === undefined ? { gridTemplateColumns: '1fr 38px' } : undefined}
    >
      {label !== undefined && <span className={s.barLabel}>{label}</span>}
      <div className={s.barTrack}>
        <div className={s.barFill} style={{ width: `${v * 100}%`, background: fill }} />
      </div>
      <span className={s.barValue}>{display ?? `${Math.round(v * 100)}%`}</span>
    </div>
  );
}

// ---- Chips & rules ------------------------------------------------------------------------------

export function Chip({
  tone,
  icon,
  children,
  title,
}: {
  tone?: 'ok' | 'warn' | 'danger' | 'brass';
  icon?: IconName;
  children: ReactNode;
  title?: string;
}) {
  const t =
    tone === 'ok'
      ? s.chipOk
      : tone === 'warn'
        ? s.chipWarn
        : tone === 'danger'
          ? s.chipDanger
          : tone === 'brass'
            ? s.chipBrass
            : undefined;
  return (
    <span className={cx(s.chip, t)} title={title}>
      {icon && <Icon name={icon} size={11} />}
      {children}
    </span>
  );
}

export function Rule({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cx(s.rule, className)}>{children}</div>;
}

export { cx };
